import { AxiosInstance, AxiosRequestConfig } from 'axios';

import { PACKAGE_URLS } from '@/constants/urls';
import FileData from '@/model/General/FileData';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';

class PackageApi {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  createAddress = async (addressData: ShippingLabel) => {
    try {
      const response = await this.axiosInstance.post('/shipping_labels', addressData);

      return response.data;
    } catch (error) {
      console.error('Error creating address:', error);
      throw error;
    }
  };

  async printShippingLabel(packageId: number, resource?: string, params?: Record<string, unknown>) {
    try {
      if (!resource) {
        throw new Error('Resource is required to print a shipping label');
      }

      const config: AxiosRequestConfig = {
        responseType: 'blob',
        params,
      };
      const response = await this.axiosInstance.get(
        `${resource}/${packageId}/shipping_label`,
        config,
      );
      const { headers, data } = response;
      const fileName = (headers['content-disposition'] as string).split('filename=')[1];
      const type = headers['content-type'] as string;
      const url = window.URL.createObjectURL(new Blob([data], { type: type }));
      const a = document.createElement('a'); // Creamos un botón para simular el click de descarga

      a.href = url;
      a.setAttribute('download', fileName);
      a.click();

      // Se limpia la memoria
      setTimeout(function () {
        window.URL.revokeObjectURL(url);
      }, 1000);

      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  async getLocationFromCEP(postalCode: string) {
    try {
      const response = await this.axiosInstance.get(PACKAGE_URLS.ADDRESS_BY_CEP, {
        params: { postal_code: postalCode, only_br: 1 },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching location from CEP:', error);
      throw error;
    }
  }

  /**
   * Metadata: sube archivos al endpoint genérico /packages_photos/, compartido
   * entre envíos y recepciones. La copia duplicada que existía en ReceptionApi
   * se eliminó el 2026-07-06 (item 9 de MEJORAS_PROPUESTAS.md) al confirmar
   * que ya no tenía ningún caller.
   * @param description - Código opcional que identifica el tipo de archivo (ej: tax_file_{id})
   */
  uploadFiles(files: FileData[], packageId: number, description?: string): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    }

    const uploadPromises: Promise<any>[] = [];

    files.forEach(file => {
      if (!file.blobFile) return;

      const formData = new FormData();

      formData.append('photo', file.blobFile);
      formData.append('package', String(packageId));
      if (description) {
        formData.append('description', description);
      }

      uploadPromises.push(
        this.axiosInstance
          .post(`${PACKAGE_URLS.PHOTOS}/`, formData)
          .then(response => response.data)
          .catch(error => {
            console.error(`Error uploading file ${file.title}:`, error);
            throw error;
          }),
      );
    });

    return Promise.all(uploadPromises);
  }

  /**
   * Metadata: elimina archivos del endpoint genérico /packages_photos/{id}.
   * La copia duplicada de ReceptionApi se eliminó el 2026-07-06 (item 9,
   * ver nota en uploadFiles).
   */
  removeFiles(files: FileData[]): Promise<any[]> {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    }

    const removePromises: Promise<any>[] = [];

    files.forEach(file => {
      removePromises.push(
        this.axiosInstance
          .delete(`${PACKAGE_URLS.PHOTOS}/${file.id}`)
          .then(response => response.data)
          .catch(error => {
            console.error(`Error removing file ${file.title}:`, error);
            throw error;
          }),
      );
    });

    return Promise.all(removePromises);
  }
}

export default PackageApi;
